import React, { useEffect, useState } from 'react';
import { Dialog } from '@material-ui/core';
import PerksTabIcon from '../../../../images/cmp-app-perks-tab.png';
import {
  APP_PROFILE_NAV, APP_ROW_ICONS, APP_SOCIALS, APP_STATUS_BAR, APP_TAB_BAR, AppSocialKey,
} from './cmpAppProfileChrome';

// MD-M19, CMP Accelerator Member Dashboard (2) ACC:1199-1220, 2507-2569 and
// "Member Profile Screen.dc.html": Preview Banners shows the company profile as the
// Chamber Perks App™ shows it. With no details and no banner, "Details Needed".

const GOLD = '#E1A731';
const RED = '#ED1C24';
const SLIDE_MS = 3000;

export type CmpProfilePreviewBanner = { image: string | null; videoUrl: string | null };

export type CmpProfilePreviewData = {
  name: string;
  contactName: string;
  phone: string;
  email: string;
  website: string;
  googleReview: string;
  street: string;
  city: string;
  postalCode: string;
  logo: string;
  socials: Partial<Record<AppSocialKey, string>>;
  banners: CmpProfilePreviewBanner[];
};

const SOCIAL_ORDER: AppSocialKey[] = ['IG', 'Threads', 'FB', 'LI', 'X', 'TT', 'YT'];

/** Design openBannerPreview: anything filled or any banner opens the preview. */
export const hasCmpProfilePreviewContent = (data: CmpProfilePreviewData) =>
  data.banners.some((banner) => banner.image || banner.videoUrl)
  || [data.name, data.contactName, data.phone, data.email, data.website, data.googleReview, data.street, data.city, data.postalCode]
    .some((value) => (value || '').trim())
  || Object.values(data.socials).some((value) => (value || '').trim());

const youTubeThumbnail = (url: string) => {
  const match = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : '';
};

const closeButton: React.CSSProperties = {
  position: 'absolute', top: 14, right: 14, width: 28, height: 28, borderRadius: '50%', background: RED, color: '#FFFFFF', border: 'none',
  cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, zIndex: 2,
};

const Html = ({ html, style }: { html: string; style?: React.CSSProperties }) => (
  // Static design markup (cmpAppProfileChrome.ts), never member data.
  // eslint-disable-next-line react/no-danger
  <div style={style} dangerouslySetInnerHTML={{ __html: html }} />
);

const Row = ({ icon, text, underline = true }: { icon: string; text: string; underline?: boolean }) => (
  <span style={{ display: 'flex', alignItems: 'center', minHeight: 56, paddingLeft: 24 }}>
    <span style={{ flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: icon }} />
    <span style={{ marginLeft: 24, fontSize: 17, color: '#111111', textDecoration: underline ? 'underline' : 'none', wordBreak: 'break-all', paddingRight: 16 }}>{text}</span>
  </span>
);

const AppProfileScreen = ({ data, index, onDot }: { data: CmpProfilePreviewData; index: number; onDot: (i: number) => void }) => {
  const slides = data.banners.filter((banner) => banner.image || banner.videoUrl);
  const address = [data.street, data.city, data.postalCode].map((value) => (value || '').trim()).filter(Boolean).join(', ');
  const socials = SOCIAL_ORDER.filter((key) => (data.socials[key] || '').trim());
  const logo = data.logo ? `url("${data.logo}")` : '';
  return (
    <div style={{ padding: 32, boxSizing: 'border-box', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'DM Sans', sans-serif" }}>
      <div style={{ width: 412, background: '#1B1B1E', borderRadius: 58, padding: 11, boxSizing: 'border-box', boxShadow: '0 30px 80px rgba(0,0,0,.5), inset 0 0 0 2px #5A5A62' }}>
        <div style={{ width: '100%', height: 844, background: '#FFFFFF', borderRadius: 48, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <Html html={APP_STATUS_BAR} style={{ flexShrink: 0 }} />
          <Html html={APP_PROFILE_NAV} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {slides.length > 0 && (
              <div style={{ position: 'relative', width: '100%', height: 250, background: '#111111', overflow: 'hidden' }} data-testid="cmp-preview-banners">
                {slides.map((slide, i) => {
                  const src = slide.image || (slide.videoUrl ? youTubeThumbnail(slide.videoUrl) : '');
                  return (
                    <div key={i} style={{ position: 'absolute', inset: 0, opacity: i === index % slides.length ? 1 : 0, transition: 'opacity 600ms ease' }}>
                      {src ? <div style={{ position: 'absolute', inset: 0, backgroundImage: `url("${src}")`, backgroundSize: 'cover', backgroundPosition: 'center' }} /> : null}
                      {slide.videoUrl ? (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="56" height="56" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="rgba(0,0,0,0.55)" /><path d="M10 8l6 4-6 4z" fill="#FFFFFF" /></svg>
                        </div>
                      ) : null}
                      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.12) 45%, rgba(0,0,0,0) 70%)', pointerEvents: 'none' }} />
                    </div>
                  );
                })}
                <div style={{ position: 'absolute', top: 16, right: 16, width: 92, height: 92, borderRadius: '50%', border: '2px solid #FFFFFF', background: 'rgba(15,15,15,0.45)', overflow: 'hidden', boxSizing: 'border-box' }}>
                  {logo ? <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundColor: '#FFFFFF', backgroundImage: logo, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center' }} /> : null}
                </div>
                {slides.length > 1 && (
                  <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 8 }}>
                    {slides.map((_, i) => (
                      <button key={i} type="button" aria-label={`Show banner ${i + 1}`} onClick={() => onDot(i)}
                        style={{ width: 8, height: 8, borderRadius: '50%', background: i === index % slides.length ? '#E0A73B' : '#FFFFFF', cursor: 'pointer', border: 'none', padding: 0 }} />
                    ))}
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '20px 24px 6px' }}>
              <span style={{ width: 52, height: 52, borderRadius: '50%', border: '1px solid #E5E5E5', overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                {logo ? <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundImage: logo, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center' }} /> : null}
              </span>
              <span style={{ fontSize: 24, fontWeight: 700, color: '#111111', lineHeight: 1.2 }}>{data.name.trim() || 'Your Company Name'}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', padding: '6px 0' }}>
              {data.contactName.trim() ? <Row icon={APP_ROW_ICONS.contact} text={data.contactName} underline={false} /> : null}
              {data.phone.trim() ? <Row icon={APP_ROW_ICONS.phone} text={data.phone} /> : null}
              {data.email.trim() ? <Row icon={APP_ROW_ICONS.email} text={data.email} /> : null}
              {data.website.trim() ? <Row icon={APP_ROW_ICONS.website} text={data.website} /> : null}
              {data.googleReview.trim() ? <Row icon={APP_ROW_ICONS.google} text="Add a Google Review" /> : null}
              {address ? <Row icon={APP_ROW_ICONS.address} text={address} /> : null}
            </div>
          </div>
          <div style={{ flexShrink: 0, background: '#FFFFFF', boxShadow: '0 -6px 16px rgba(0,0,0,.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, padding: '14px 0' }}>
              {socials.map((key) => (
                <span key={key} data-testid={`cmp-preview-social-${key}`} style={{ width: 44, height: 44, borderRadius: APP_SOCIALS[key].radius, background: APP_SOCIALS[key].bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }} dangerouslySetInnerHTML={{ __html: APP_SOCIALS[key].svg }} />
              ))}
            </div>
            <div style={{ padding: '0 16px 14px' }}>
              <div style={{ width: '100%', height: 62, borderRadius: 31, background: '#E0A73B', color: '#FFFFFF', fontSize: 22, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Offering Perks</div>
            </div>
          </div>
          <Html html={APP_TAB_BAR.replace('__PERKS_TAB__', PerksTabIcon)} style={{ flexShrink: 0 }} />
        </div>
      </div>
    </div>
  );
};

const previewScale = () => (typeof window === 'undefined' ? 0.75
  : Math.max(0.35, Math.min(0.75, (window.innerHeight * 0.92 - 70) / 950, (window.innerWidth * 0.8) / 476)));

export const CmpProfileBannerPreview = ({ open, onClose, data }: { open: boolean; onClose: () => void; data: CmpProfilePreviewData }) => {
  const [index, setIndex] = useState(0);
  const [scale, setScale] = useState(previewScale);
  const filled = hasCmpProfilePreviewContent(data);
  const slideCount = data.banners.filter((banner) => banner.image || banner.videoUrl).length;

  useEffect(() => {
    if (!open) return undefined;
    setIndex(0);
    setScale(previewScale());
    const resize = () => setScale(previewScale());
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [open]);

  useEffect(() => {
    if (!open || !filled || slideCount < 2) return undefined;
    const timer = window.setInterval(() => setIndex((value) => (value + 1) % slideCount), SLIDE_MS);
    return () => window.clearInterval(timer);
  }, [open, filled, slideCount]);

  if (!open) return null;

  if (!filled) {
    return (
      <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ style: { borderRadius: 16, width: 'min(440px, 90vw)', margin: 0 } }}>
        <div data-testid="cmp-preview-details-needed" style={{ padding: '28px 32px 32px', boxSizing: 'border-box', position: 'relative', display: 'flex',
          flexDirection: 'column', alignItems: 'center', gap: 16, fontFamily: "'DM Sans', sans-serif" }}>
          <button type="button" aria-label="Close" onClick={onClose} style={closeButton}>✕</button>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 6 }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9.5" /><path d="M12 7.5v5.5M12 16.5h.01" /></svg>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#000000', textAlign: 'center' }}>Details Needed</div>
          <div style={{ fontSize: 14, color: '#000000', textAlign: 'center', lineHeight: 1.7 }}>
            Please add details to preview blocks. Fill in your company profile information or upload at least one banner to see the preview.
          </div>
          <button type="button" onClick={onClose} style={{ background: GOLD, color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '11px 32px',
            fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600, letterSpacing: '0.04em', cursor: 'pointer', marginTop: 4 }}>OK, Got It</button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ style: { borderRadius: 16, margin: 0, overflow: 'hidden', maxHeight: '92vh' } }}
      BackdropProps={{ style: { background: 'rgba(0,0,0,0.55)' } }}>
      <div data-testid="cmp-profile-banner-preview" style={{ position: 'relative', background: '#FFFFFF', padding: '20px 28px 8px', boxSizing: 'border-box', fontFamily: "'DM Sans', sans-serif" }}>
        <button type="button" aria-label="Close" onClick={onClose} style={closeButton}>✕</button>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#000000', textAlign: 'center' }}>Banner Preview</div>
        <div style={{ width: 476 * scale, height: 930 * scale, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', overflow: 'hidden' }}>
          <div style={{ transform: `scale(${scale})`, transformOrigin: 'center center', flexShrink: 0 }}>
            <AppProfileScreen data={data} index={index} onDot={setIndex} />
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default CmpProfileBannerPreview;
